/**
 * OmniClinicalBridge Telemedicine Module
 * OmniAlera Technologies Holdings Inc.
 * 
 * Video consultation infrastructure using WebRTC
 * Enables remote specialist consultations across Africa
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';

// Consultation status
export type ConsultationStatus = 
  | 'idle'
  | 'connecting'
  | 'waiting'
  | 'in-progress'
  | 'on-hold'
  | 'ended'
  | 'error';

// Participant type
export interface Participant {
  id: string;
  name: string;
  role: 'patient' | 'physician' | 'specialist' | 'nurse' | 'interpreter';
  isLocal: boolean;
  isMuted: boolean;
  isVideoOff: boolean;
  stream?: MediaStream;
}

// Consultation data
export interface Consultation {
  id: string;
  scheduledAt: Date;
  startedAt?: Date;
  endedAt?: Date;
  status: ConsultationStatus;
  participants: Participant[];
  type: 'primary' | 'specialist' | 'emergency' | 'follow-up';
  specialty?: string;
  notes: ConsultationNote[];
  recordings?: string[];
}

// Consultation notes
export interface ConsultationNote {
  id: string;
  timestamp: Date;
  authorId: string;
  content: string;
  type: 'observation' | 'diagnosis' | 'recommendation' | 'prescription' | 'referral';
}

// Context type
interface TelemedicineContextType {
  // State
  currentConsultation: Consultation | null;
  localStream: MediaStream | null;
  remoteStreams: Map<string, MediaStream>;
  status: ConsultationStatus;
  error: string | null;
  
  // Media controls
  startLocalMedia: () => Promise<boolean>;
  stopLocalMedia: () => void;
  toggleMute: () => void;
  toggleVideo: () => void;
  isMuted: boolean;
  isVideoOff: boolean;
  
  // Consultation management
  startConsultation: (consultationId: string) => Promise<boolean>;
  endConsultation: () => void;
  joinConsultation: (consultationId: string) => Promise<boolean>;
  leaveConsultation: () => void;
  
  // Features
  addNote: (note: Omit<ConsultationNote, 'id' | 'timestamp'>) => void;
  requestSpecialist: (specialty: string) => Promise<boolean>;
  shareScreen: () => Promise<boolean>;
  stopScreenShare: () => void;
  isScreenSharing: boolean;
}

const TelemedicineContext = createContext<TelemedicineContextType | undefined>(undefined);

// Generate IDs
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const TelemedicineProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentConsultation, setCurrentConsultation] = useState<Consultation | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(new Map());
  const [status, setStatus] = useState<ConsultationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  
  const peerConnections = useRef<Map<string, RTCPeerConnection>>(new Map());
  const screenStream = useRef<MediaStream | null>(null);
  
  // Start local media (camera + microphone)
  const startLocalMedia = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      setLocalStream(stream);
      setError(null);
      return true;
    } catch (err) {
      console.error('[Telemedicine] Failed to access media devices:', err);
      setError('Could not access camera or microphone. Please check permissions.');
      return false;
    }
  }, []);
  
  // Stop local media
  const stopLocalMedia = useCallback(() => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
  }, [localStream]);
  
  // Toggle mute
  const toggleMute = useCallback(() => {
    if (localStream) {
      const audioTracks = localStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  }, [localStream, isMuted]);
  
  // Toggle video
  const toggleVideo = useCallback(() => {
    if (localStream) {
      const videoTracks = localStream.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsVideoOff(!isVideoOff);
    }
  }, [localStream, isVideoOff]);
  
  // Start consultation
  const startConsultation = useCallback(async (_consultationId: string): Promise<boolean> => {
    try {
      setStatus('connecting');
      
      // Start local media first
      const mediaStarted = await startLocalMedia();
      if (!mediaStarted) {
        setStatus('error');
        return false;
      }
      
      // Create consultation
      const consultation: Consultation = {
        id: `consultation-${Date.now()}`,
        scheduledAt: new Date(),
        startedAt: new Date(),
        status: 'in-progress',
        participants: [],
        type: 'primary',
        notes: []
      };
      
      setCurrentConsultation(consultation);
      setStatus('in-progress');
      
      // In production, would connect to signaling server here
      console.log('[Telemedicine] Consultation started:', consultation.id);
      
      return true;
    } catch (err) {
      console.error('[Telemedicine] Failed to start consultation:', err);
      setError('Failed to start consultation');
      setStatus('error');
      return false;
    }
  }, [startLocalMedia]);
  
  // End consultation
  const endConsultation = useCallback(() => {
    // Stop all media
    stopLocalMedia();
    
    // Close peer connections
    peerConnections.current.forEach(pc => pc.close());
    peerConnections.current.clear();
    
    // Clear remote streams
    setRemoteStreams(new Map());
    
    // Update consultation
    if (currentConsultation) {
      setCurrentConsultation({
        ...currentConsultation,
        endedAt: new Date(),
        status: 'ended'
      });
    }
    
    setStatus('ended');
    console.log('[Telemedicine] Consultation ended');
  }, [currentConsultation, stopLocalMedia]);
  
  // Join consultation
  const joinConsultation = useCallback(async (_consultationId: string): Promise<boolean> => {
    try {
      setStatus('connecting');
      
      const mediaStarted = await startLocalMedia();
      if (!mediaStarted) {
        setStatus('error');
        return false;
      }
      
      // In production, would connect to existing consultation via signaling server
      setStatus('in-progress');
      return true;
    } catch (err) {
      console.error('[Telemedicine] Failed to join consultation:', err);
      setError('Failed to join consultation');
      setStatus('error');
      return false;
    }
  }, [startLocalMedia]);
  
  // Leave consultation
  const leaveConsultation = useCallback(() => {
    stopLocalMedia();
    peerConnections.current.forEach(pc => pc.close());
    peerConnections.current.clear();
    setRemoteStreams(new Map());
    setStatus('idle');
    setCurrentConsultation(null);
  }, [stopLocalMedia]);
  
  // Add note
  const addNote = useCallback((note: Omit<ConsultationNote, 'id' | 'timestamp'>) => {
    if (!currentConsultation) return;
    
    const newNote: ConsultationNote = {
      ...note,
      id: generateId(),
      timestamp: new Date()
    };
    
    setCurrentConsultation(prev => prev ? {
      ...prev,
      notes: [...prev.notes, newNote]
    } : null);
  }, [currentConsultation]);
  
  // Request specialist
  const requestSpecialist = useCallback(async (specialty: string): Promise<boolean> => {
    console.log('[Telemedicine] Requesting specialist:', specialty);
    // In production, would send request to scheduling system
    return true;
  }, []);
  
  // Share screen
  const shareScreen = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false
      });
      
      screenStream.current = stream;
      setIsScreenSharing(true);
      
      // Listen for stream end
      stream.getVideoTracks()[0].onended = () => {
        setIsScreenSharing(false);
        screenStream.current = null;
      };
      
      return true;
    } catch (err) {
      console.error('[Telemedicine] Screen share failed:', err);
      return false;
    }
  }, []);
  
  // Stop screen share
  const stopScreenShare = useCallback(() => {
    if (screenStream.current) {
      screenStream.current.getTracks().forEach(track => track.stop());
      screenStream.current = null;
      setIsScreenSharing(false);
    }
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopLocalMedia();
      peerConnections.current.forEach(pc => pc.close());
    };
  }, [stopLocalMedia]);
  
  const value: TelemedicineContextType = {
    currentConsultation,
    localStream,
    remoteStreams,
    status,
    error,
    startLocalMedia,
    stopLocalMedia,
    toggleMute,
    toggleVideo,
    isMuted,
    isVideoOff,
    startConsultation,
    endConsultation,
    joinConsultation,
    leaveConsultation,
    addNote,
    requestSpecialist,
    shareScreen,
    stopScreenShare,
    isScreenSharing
  };
  
  return (
    <TelemedicineContext.Provider value={value}>
      {children}
    </TelemedicineContext.Provider>
  );
};

export const useTelemedicine = (): TelemedicineContextType => {
  const context = useContext(TelemedicineContext);
  if (!context) {
    throw new Error('useTelemedicine must be used within a TelemedicineProvider');
  }
  return context;
};

/**
 * Video Component
 * Displays local or remote video stream
 */
export const VideoStream: React.FC<{
  stream: MediaStream | null;
  isLocal?: boolean;
  isMuted?: boolean;
  className?: string;
}> = ({ stream, isLocal = false, isMuted = false, className = '' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);
  
  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted={isLocal || isMuted}
      className={`w-full h-full object-cover ${isLocal ? 'transform scale-x-[-1]' : ''} ${className}`}
    />
  );
};
